import { useEffect, useState } from 'react'

const MAX_STORED_MESSAGES = 80

function pendingSuggestions(suggestions = []) {
  return suggestions.map((suggestion) => ({
    ...suggestion,
    status: 'pending',
  }))
}

function restoredMessages(messages) {
  return messages.map((message) => ({
    ...message,
    fieldSuggestions: (message.fieldSuggestions || []).map((suggestion) => ({
      ...suggestion,
      status: suggestion.status === 'applied' ? 'pending' : suggestion.status,
    })),
  }))
}

function storedMessages(storageKey, initialMessages) {
  if (!storageKey || typeof window === 'undefined') return restoredMessages(initialMessages)

  try {
    const storedValue = window.localStorage.getItem(storageKey)
      ?? window.sessionStorage.getItem(storageKey)
    const parsedMessages = JSON.parse(storedValue)
    return Array.isArray(parsedMessages) ? restoredMessages(parsedMessages) : restoredMessages(initialMessages)
  } catch {
    return restoredMessages(initialMessages)
  }
}

function removeStoredMessages(storageKey) {
  if (!storageKey || typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(storageKey)
    window.sessionStorage.removeItem(storageKey)
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
}

function persistStoredMessages(storageKey, messages) {
  if (!storageKey || typeof window === 'undefined') return

  try {
    if (messages.length === 0) {
      window.localStorage.removeItem(storageKey)
      window.sessionStorage.removeItem(storageKey)
      return
    }

    window.localStorage.setItem(
      storageKey,
      JSON.stringify(messages.slice(-MAX_STORED_MESSAGES)),
    )
    window.sessionStorage.removeItem(storageKey)
  } catch {
    // Keep chat usable when session storage is unavailable or full.
  }
}

export function useAskRobChat({ initialMessages = [], storageKey = null } = {}) {
  const [messages, setMessages] = useState(() => storedMessages(storageKey, initialMessages))
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const initialMessagesSignature = JSON.stringify(initialMessages)
  const [restoredInitialMessagesSignature, setRestoredInitialMessagesSignature] = useState(
    initialMessagesSignature,
  )

  if (restoredInitialMessagesSignature !== initialMessagesSignature) {
    setRestoredInitialMessagesSignature(initialMessagesSignature)
    if (messages.length === 0 && initialMessages.length > 0) {
      setMessages(restoredMessages(initialMessages))
    }
  }

  useEffect(() => {
    persistStoredMessages(storageKey, messages)
  }, [messages, storageKey])

  const updateSuggestionStatus = (messageIndex, suggestionId, status) => {
    setMessages((currentMessages) => currentMessages.map((message, index) => {
      if (index !== messageIndex) return message

      return {
        ...message,
        fieldSuggestions: (message.fieldSuggestions || []).map((suggestion) => (
          suggestion.id === suggestionId ? { ...suggestion, status } : suggestion
        )),
      }
    }))
  }

  const dismissSuggestion = (messageIndex, suggestionId) => {
    updateSuggestionStatus(messageIndex, suggestionId, 'dismissed')
  }

  const findSuggestion = (messageIndex, suggestionId) => (
    messages[messageIndex]?.fieldSuggestions?.find((suggestion) => suggestion.id === suggestionId)
  )

  const submitMessage = async (message, request) => {
    const nextMessages = [...messages, { role: 'user', content: message }]
    persistStoredMessages(storageKey, nextMessages)
    setMessages(nextMessages)
    setError(null)
    setIsLoading(true)

    try {
      const response = await request({ message, conversation: messages })
      const completedMessages = [
        ...nextMessages,
        {
          role: 'assistant',
          content: response.assistantMessage,
          fieldSuggestions: pendingSuggestions(response.fieldSuggestions),
          lifestyleSummary: response.lifestyleSummary,
        },
      ]
      persistStoredMessages(storageKey, completedMessages)
      setMessages(completedMessages)
      return response
    } catch (requestError) {
      setError(requestError.message)
      setMessages(nextMessages)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  const clearMessages = () => {
    removeStoredMessages(storageKey)
    setMessages([])
    setError(null)
  }

  return {
    messages,
    error,
    isLoading,
    submitMessage,
    findSuggestion,
    updateSuggestionStatus,
    dismissSuggestion,
    clearMessages,
  }
}
